import { AiChat, AiChatHeader, useAiChatContext } from 'juneau';
import { Icon } from '@insio/ui-toolkit';
import { renderPart } from './parts/part-registry';
import { TappeerEMark } from './TappeerBrandMark';
import { GetStartedState } from './GetStartedState';
import { ChatSettingsButton } from './ChatSettingsButton';
import { OnlineIndicator } from './OnlineIndicator';
import { useChatReset } from './use-chat-reset';
import { useChatInputActions } from './use-chat-input-actions';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/**
 * Docked copilot column — the same conversation as the side panel, rendered as a
 * permanent right-hand column that takes its own layout space instead of overlaying.
 */
interface DockedChatPanelProps {
  /** True while the column is collapsed to its rail. Shared with the bubble surface. */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

export function DockedChatPanel({ collapsed, onCollapsedChange }: DockedChatPanelProps) {
  const {
    messages,
    input,
    setInput,
    sendMessage,
    stop,
    isLoading,
    isConnecting,
    error,
    reset,
    confirmProposal,
    cancelProposal
  } = useAiChatContext();

  // `reset` must be handed over: this panel renders the chat body itself, so nothing
  // else drops the on-screen conversation — clearing only the server history left the
  // messages up until a reload.
  const { handleReset, dialog: resetConfirmDialog } = useChatReset(reset);
  const { actions: inputActions, dialogs: inputActionDialogs } = useChatInputActions();
  const t = useFeatureTranslation('aiChat');

  // A rail instead of the column: the width is what the page gets back, so the chat
  // body unmounts rather than hiding. The conversation lives in the provider above.
  if (collapsed) {
    return (
      <aside
        data-testid='docked-chat-panel-collapsed'
        className='flex h-full w-7 shrink-0 flex-col items-center border-l border-border-base bg-background-input'
      >
        <button
          type='button'
          aria-label={t('expandPanel')}
          aria-expanded={false}
          title={t('expandPanel')}
          data-testid='docked-chat-expand'
          onClick={() => onCollapsedChange(false)}
          className='mt-md flex size-7 items-center justify-center rounded text-foreground-muted transition-colors hover:bg-background-muted hover:text-foreground-base'
        >
          <Icon name='chevron-down' rotate={90} className='h-4 w-4' />
        </button>
      </aside>
    );
  }

  return (
    <aside
      data-testid='docked-chat-panel'
      // Width climbs with the viewport: on a small laptop the column has to leave the
      // app enough room, so it starts narrow and only widens once there is space.
      // `shrink-0` holds the width — a min-width would clamp the narrow steps.
      className='flex h-full w-[340px] shrink-0 flex-col overflow-hidden border-l border-border-base bg-background-input xl:w-[420px] 2xl:w-[520px]'
    >
      <AiChatHeader
        icon={<TappeerEMark size={18} chipTone='hero' />}
        onReset={handleReset}
        headerActions={
          <>
            <OnlineIndicator />
            <ChatSettingsButton />
            <button
              type='button'
              aria-label={t('collapsePanel')}
              aria-expanded={true}
              title={t('collapsePanel')}
              data-testid='docked-chat-collapse'
              onClick={() => onCollapsedChange(true)}
              className='flex size-7 items-center justify-center rounded text-inherit opacity-70 transition-opacity hover:opacity-100'
            >
              <Icon name='chevron-down' rotate={270} className='h-4 w-4' />
            </button>
          </>
        }
      />
      <AiChat
        messages={messages}
        input={input}
        isLoading={isLoading}
        isConnecting={isConnecting}
        error={error}
        onInputChange={setInput}
        onSend={sendMessage}
        onStop={stop}
        onProposalConfirm={confirmProposal}
        onProposalCancel={cancelProposal}
        assistantIcon={<TappeerEMark size={18} chip={false} />}
        renderPart={renderPart}
        actions={inputActions}
        emptyState={<GetStartedState compact />}
      />
      {resetConfirmDialog}
      {inputActionDialogs}
    </aside>
  );
}
